import { Formatters, NumberFormat } from '@billy/design-system'
import { useMemo } from 'react'

import { Account } from '@modules-deprecated/app/accounts/types'
import { TaxRate } from '@modules-deprecated/app/taxrates/types'

import { VatSuggestion } from '../../../hooks/useFetchSuggestions'
import { SuggestionsTableAccessor } from '../enums/SuggestionsTableAccessor'
import { SuggestionsTableRow } from '../types/SuggestionsTableRow'

interface UseTableDataProps {
  accounts: Account[] | undefined
  data: VatSuggestion[]
  locale: string | undefined
  taxRates: TaxRate[] | undefined
}

export const useTableData = ({ accounts, data, locale, taxRates }: UseTableDataProps): SuggestionsTableRow[] => {
  return useMemo(
    () =>
      data
        .sort((a, b) => b.confidence - a.confidence)
        .map(
          ({
            amount,
            attachmentId,
            confidence,
            currentAccountId,
            currentTaxRateId,
            id,
            isApproved,
            isRejected,
            subjectDescription,
            suggestedTaxRateId,
            suggestionDescription,
          }) => {
            /* Account */
            const currentAccount = accounts?.find((account) => account.id === currentAccountId)
            const accountLabel = currentAccount ? `${currentAccount.accountNo} - ${currentAccount.name}` : ''

            /* Tax rates */
            const currentTaxRate = taxRates?.find((taxRate) => taxRate.id === currentTaxRateId)
            const suggestedVatRate = taxRates?.find((taxRate) => taxRate.id === suggestedTaxRateId)

            const formattedAmount = amount
              ? NumberFormat.getFormattedNumber(amount, Formatters.getFormatLocale(locale || 'en_US'))
              : '0'

            return {
              id,
              [SuggestionsTableAccessor.Receipt]: attachmentId,
              [SuggestionsTableAccessor.Amount]: formattedAmount,
              [SuggestionsTableAccessor.Description]: subjectDescription,
              [SuggestionsTableAccessor.Account]: accountLabel,
              [SuggestionsTableAccessor.Score]: {
                rate: Math.round(confidence * 100),
                info: suggestionDescription,
              },
              [SuggestionsTableAccessor.CurrentVat]: currentTaxRate?.name || '',
              [SuggestionsTableAccessor.SuggestedVat]: suggestedVatRate?.id || suggestedTaxRateId,
              [SuggestionsTableAccessor.Approve]: isApproved,
              [SuggestionsTableAccessor.Rejected]: isRejected,
            }
          },
        ),
    [accounts, data, locale, taxRates],
  )
}
